import { useQuery } from '@tanstack/react-query';
import { format, addDays } from 'date-fns';
import { getAppointmentClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import type { ScheduleQuery } from '@vagusx/clients';

export interface ScheduleFilters {
  startDate: Date;
  endDate: Date;
  providerIds?: string[];
  locationIds?: string[];
  statuses?: number[];
  search?: string;
}

/**
 * Hook to fetch appointment schedule data.
 */
export function useSchedule(filters: ScheduleFilters) {
  const query: ScheduleQuery = {
    startDate: format(filters.startDate, 'yyyy-MM-dd'),
    endDate: format(filters.endDate, 'yyyy-MM-dd'),
    providerIds: filters.providerIds,
    locationIds: filters.locationIds,
    statuses: filters.statuses,
    search: filters.search,
  };

  return useQuery({
    queryKey: queryKeys.appointments.schedule(query),
    queryFn: () => getAppointmentClient().getSchedule(query),
  });
}

/**
 * Default date range: today + 30 days.
 */
export function getDefaultDateRange(): { startDate: Date; endDate: Date } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return {
    startDate: today,
    endDate: addDays(today, 30),
  };
}

/**
 * Appointment status constants (matching backend).
 */
export const APPOINTMENT_STATUS = {
  PROPOSED: 0,
  PENDING: 1,
  BOOKED: 2,
  ARRIVED: 3,
  FULFILLED: 4,
  CANCELLED: 5,
  NOSHOW: 6,
  ENTERED_IN_ERROR: 7,
} as const;

export const APPOINTMENT_STATUS_LABELS: Record<number, string> = {
  [APPOINTMENT_STATUS.PROPOSED]: 'Proposed',
  [APPOINTMENT_STATUS.PENDING]: 'Pending',
  [APPOINTMENT_STATUS.BOOKED]: 'Booked',
  [APPOINTMENT_STATUS.ARRIVED]: 'Arrived',
  [APPOINTMENT_STATUS.FULFILLED]: 'Fulfilled',
  [APPOINTMENT_STATUS.CANCELLED]: 'Cancelled',
  [APPOINTMENT_STATUS.NOSHOW]: 'No Show',
  [APPOINTMENT_STATUS.ENTERED_IN_ERROR]: 'Error',
};

export const APPOINTMENT_STATUS_COLORS: Record<number, { bg: string; text: string }> = {
  [APPOINTMENT_STATUS.PROPOSED]: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    text: 'text-gray-700 dark:text-gray-300',
  },
  [APPOINTMENT_STATUS.PENDING]: {
    bg: 'bg-amber-100 dark:bg-amber-900/40',
    text: 'text-amber-700 dark:text-amber-400',
  },
  [APPOINTMENT_STATUS.BOOKED]: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/40',
    text: 'text-emerald-700 dark:text-emerald-400',
  },
  [APPOINTMENT_STATUS.ARRIVED]: {
    bg: 'bg-blue-100 dark:bg-blue-900/40',
    text: 'text-blue-700 dark:text-blue-400',
  },
  [APPOINTMENT_STATUS.FULFILLED]: {
    bg: 'bg-teal-100 dark:bg-teal-900/40',
    text: 'text-teal-700 dark:text-teal-400',
  },
  [APPOINTMENT_STATUS.CANCELLED]: {
    bg: 'bg-red-100 dark:bg-red-900/40',
    text: 'text-red-700 dark:text-red-400',
  },
  [APPOINTMENT_STATUS.NOSHOW]: {
    bg: 'bg-orange-100 dark:bg-orange-900/40',
    text: 'text-orange-700 dark:text-orange-400',
  },
  [APPOINTMENT_STATUS.ENTERED_IN_ERROR]: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    text: 'text-gray-500 dark:text-gray-500',
  },
};
